import { useNavigate, useLocation } from 'react-router-dom';
import { useParams } from '@/hooks/useParams';

// Re-export for easier migration
export { useNavigate, useLocation, useParams };

// Note: Import Link from '@/lib/router' directly, not from here.
// This avoids circular dependencies with TabLink.tsx → navigation.ts → router.ts

// Create a history-like object for compatibility
let navigateRef: ReturnType<typeof useNavigate> | null = null;
type HistoryNavigationInterceptor = (
  method: 'push' | 'replace',
  path: string,
  state?: any
) => boolean;
let historyNavigationInterceptor: HistoryNavigationInterceptor | null = null;

export function setHistoryNavigationInterceptor(interceptor: HistoryNavigationInterceptor | null) {
  historyNavigationInterceptor = interceptor;
}

export const history = {
  push: (path: any, state?: any) => {
    if (typeof path === 'string' && historyNavigationInterceptor?.('push', path, state)) {
      return;
    }
    if (navigateRef) {
      navigateRef(path, { state });
    } else {
      window.location.href = path;
    }
  },
  replace: (path: any, state?: any) => {
    if (typeof path === 'string' && historyNavigationInterceptor?.('replace', path, state)) {
      return;
    }
    if (navigateRef) {
      navigateRef(path, { replace: true, state });
    } else {
      window.location.replace(path);
    }
  },
  go: (delta: number) => window.history.go(delta),
  back: () => window.history.back(),
  forward: () => window.history.forward(),
  get location() {
    return window.location;
  }
};

// Hook to initialize the navigate ref
export function useHistoryInit() {
  const navigate = useNavigate();
  navigateRef = navigate;
  return navigate;
}
